"use client";

import { useTranslations } from "next-intl";

import { Link } from "@/shared/i18n/routing";
import FinzyIcon from "@/shared/icons/logos/finzy.svg";
import { ComponentProps, PropsWithChildren } from "react";
import { Button } from "@/shared/components/button";
import BrandTelegramIcon from "@/shared/icons/brand-telegram-2.svg";
import { tp } from "@/shared/utils/typograf";
import { documentLinks } from "@/shared/constants/documentLinks";
import { TELEGRAM_NEWS_URL } from "@/shared/constants/telegram";

const mainLinks = [
  { href: "#purchase", key: "purchase" },
  { href: "#transfers", key: "transfers" },
  { href: "#payments", key: "payments" },
  { href: "#services", key: "services" },
  { href: "#opportunities", key: "opportunities" }
] as const;

const footerDocumentLinks = [
  { href: documentLinks.termsOfService, key: "termsOfService" },
  { href: documentLinks.amlCftPolicy, key: "amlCftPolicy" },
  { href: documentLinks.cookiePolicy, key: "cookiePolicy" },
  { href: documentLinks.riskNotice, key: "riskNotice" },
  { href: documentLinks.privacyPolicy, key: "privacyPolicy" }
] as const;

const FooterTitle = (props: PropsWithChildren) => (
  <h5 className="text-sm leading-5 font-medium">{props.children}</h5>
);

const FooterLink = (props: ComponentProps<typeof Link>) => (
  <Link
    {...props}
    /* wrap-anywhere: на 320px длинные названия документов не влезают в колонку. */
    className="inline-flex wrap-anywhere py-2 group-first:pt-0 group-last:pb-0 text-[#7B7B87] text-sm leading-5 hover:text-white transition ease-in-out hover:underline"
  />
);

export const Footer = () => {
  const t = useTranslations("footer");
  const tNav = useTranslations("common.nav");
  const tDocuments = useTranslations("footer.groups.documents.links");

  return (
    <div className="section-container py-10 space-y-8 lg:space-y-12.5">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10 xl:gap-25">
        <Link href="/" className="inline-flex pressable">
          <FinzyIcon className="w-16 h-8" />
        </Link>
        {/* На мобильном две навигации стоят рядом; на десктопе обёртка исчезает. */}
        <div className="grid grid-cols-2 gap-6 lg:contents">
          <nav className="space-y-4">
            <FooterTitle>{tp.execute(t("groups.main.title"))}</FooterTitle>
            <ul>
              {mainLinks.map((link) => (
                <li key={link.key} className="group">
                  <FooterLink href={link.href}>
                    {tp.execute(tNav(link.key))}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
          <nav className="space-y-4">
            <FooterTitle>{tp.execute(t("groups.documents.title"))}</FooterTitle>
            <ul>
              {footerDocumentLinks.map((link) => (
                <li key={link.key} className="group">
                  <FooterLink
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tp.execute(tDocuments(link.key))}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div>
          <ul className="space-y-2">
            <li>
              <FooterTitle>{tp.execute(t("support.title"))}</FooterTitle>
              <FooterLink href={`mailto:${t("support.email")}`}>
                {tp.execute(t("support.email"))}
              </FooterLink>
            </li>
            <li>
              <FooterTitle>{tp.execute(t("workingHours.title"))}</FooterTitle>
              <span className="inline-flex py-2 text-[#7B7B87] text-sm leading-5">
                {tp.execute(t("workingHours.value"))}
              </span>
            </li>
            <li>
              <FooterTitle>
                {tp.execute(t("complianceRequests.title"))}
              </FooterTitle>
              <FooterLink href={`mailto:${t("complianceRequests.email")}`}>
                {tp.execute(t("complianceRequests.email"))}
              </FooterLink>
            </li>
          </ul>
        </div>
        <Button
          variant="secondary"
          className="w-full lg:w-auto lg:ml-auto"
          render={
            <a
              href={TELEGRAM_NEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
            />
          }
        >
          <BrandTelegramIcon className="size-6" />{" "}
          <span>{tp.execute(t("newsButton"))}</span>
        </Button>
      </div>
    </div>
  );
};
